import { useEffect, useRef } from "react";
import { MapIconMarker, MapCardMarker } from "types/map";
import MapMarkerCard from "components/map/MapMarkerCard";
import MapMarkerIcon from "components/map/MapMarkerIcon";
import { useAppSelector } from "redux/hooks";
import { selectReferenceExperience } from "redux/slices/shed/shedSlice";

type Props = {
  map: google.maps.Map | null; // Map instance passed from MapContainer
  onCardClick: (cardMarker: MapCardMarker) => void;
};

const ZOOM_LEVEL_SINGLE_MARKER = 14;
const USER_LOCATION_DEFAULT_ZOOM = 10;

const MapMarkersExperience = ({ map, onCardClick }: Props) => {
  const boundsRef = useRef<google.maps.LatLngBounds | null>(null);

  const {
    experienceId,
    cardMarkers,
    defaultZoom: experienceDefaultZoom,
    iconMarkers,
  } = useAppSelector(selectReferenceExperience);

  useEffect(() => {
    if (map && (cardMarkers.length > 1 || iconMarkers.length > 1)) {
      /**
       * Only FitBound markers if no User Location
       * In other words: Do not override the zoom and user location marker
       */
      navigator.geolocation &&
        navigator.geolocation.getCurrentPosition(
          () => {
            /**
             * Geolocation: When you have user location available
             * [onNormalView] If user location
             */
            map && map.setZoom(USER_LOCATION_DEFAULT_ZOOM);
          },
          () => {
            /**
             * Geolocation: When do NOT have user location available
             * [onEmbed] in No location
             */
            boundsRef.current = !boundsRef.current
              ? new google.maps.LatLngBounds()
              : boundsRef.current;

            // Extend the bounds to include each marker's position
            [...cardMarkers, ...iconMarkers].forEach((marker) => {
              boundsRef.current?.extend(
                new google.maps.LatLng(marker.lat, marker.lng)
              );
            });
            // Fit the map to the calculated bounds
            map.fitBounds(boundsRef.current);
          }
        );
    } else if (map && cardMarkers.length === 1) {
      map.setCenter({ lat: cardMarkers[0].lat, lng: cardMarkers[0].lng });
      !experienceDefaultZoom && map.setZoom(ZOOM_LEVEL_SINGLE_MARKER);
    } else if (map && iconMarkers.length === 1) {
      map.setCenter({ lat: iconMarkers[0].lat, lng: iconMarkers[0].lng });
      !experienceDefaultZoom && map.setZoom(ZOOM_LEVEL_SINGLE_MARKER);
    }
    map && experienceDefaultZoom && map.setZoom(experienceDefaultZoom);
  }, [map, experienceDefaultZoom, cardMarkers, iconMarkers]);

  return (
    <>
      {iconMarkers.map((marker: MapIconMarker, index: number) => (
        <MapMarkerIcon key={experienceId + index} map={map} marker={marker} />
      ))}
      {cardMarkers.map((marker: MapCardMarker, index: number) => (
        <MapMarkerCard
          onCardClick={onCardClick}
          key={experienceId + index}
          map={map}
          marker={marker}
        />
      ))}
    </>
  );
};

export default MapMarkersExperience;
